import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Switch,
  Alert,
  FormControlLabel,
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { Add, Edit, Delete } from '@mui/icons-material'
import { PageHeader } from '../../components/PageHeader'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import {
  useRules,
  useCreateRule,
  useUpdateRule,
  useDeleteRule,
  useToggleRuleStatus,
  Rule,
} from '../../hooks/useRules'
import { format } from 'date-fns'

const EVENT_TYPES = ['sensor', 'location', 'status']

const defaultRule = {
  name: '',
  description: '',
  event_type: 'sensor',
  active: true,
  conditions: {
    operator: 'AND',
    conditions: [{ key: 'temperature', op: '>', value: 80 }],
  },
  actions: [{ type: 'ALERT', severity: 'critical' }],
  notify_driver: false,
}

export default function RulesPage() {
  const { t } = useTranslation()
  const { data: rules = [], isLoading } = useRules()
  const createRule = useCreateRule()
  const updateRule = useUpdateRule()
  const deleteRule = useDeleteRule()
  const toggleStatus = useToggleRuleStatus()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Rule | null>(null)
  const [form, setForm] = useState(defaultRule)
  const [conditionsJson, setConditionsJson] = useState(
    JSON.stringify(defaultRule.conditions, null, 2),
  )
  const [actionsJson, setActionsJson] = useState(JSON.stringify(defaultRule.actions, null, 2))
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [jsonError, setJsonError] = useState('')

  const openCreate = () => {
    setEditing(null)
    setForm(defaultRule)
    setConditionsJson(JSON.stringify(defaultRule.conditions, null, 2))
    setActionsJson(JSON.stringify(defaultRule.actions, null, 2))
    setJsonError('')
    setDialogOpen(true)
  }

  const openEdit = (r: Rule) => {
    setEditing(r)
    setForm({
      name: r.name,
      description: r.description,
      event_type: r.event_type,
      active: r.enabled ?? true,
      conditions: r.conditions as typeof defaultRule.conditions,
      actions: r.actions as typeof defaultRule.actions,
      notify_driver: (r as any).notify_driver ?? (r.actions as any)?.[0]?.notify_driver ?? false,
    })
    setConditionsJson(JSON.stringify(r.conditions, null, 2))
    setActionsJson(JSON.stringify(r.actions, null, 2))
    setJsonError('')
    setDialogOpen(true)
  }

  const handleSubmit = async () => {
    try {
      const conditions = JSON.parse(conditionsJson)
      const actions = JSON.parse(actionsJson).map((a: any) => ({
        ...a,
        notify_driver: form.notify_driver,
      }))
      const { active, notify_driver, ...rest } = form
      const payload = { ...rest, conditions, actions }
      if (editing) {
        await updateRule.mutateAsync({ id: editing.id, ...payload } as any)
      } else {
        await createRule.mutateAsync(payload)
        analytics.track(EVENTS.RULE_CREATED, { rule_name: form.name, event_type: form.event_type })
      }
      setDialogOpen(false)
    } catch {
      setJsonError('Invalid JSON in conditions or actions')
    }
  }

  const columns: GridColDef[] = [
    { field: 'name', headerName: t('rules.title'), flex: 1, minWidth: 180 },
    { field: 'description', headerName: t('common.description'), flex: 1, minWidth: 180 },
    {
      field: 'event_type',
      headerName: t('rules.eventType'),
      width: 130,
      renderCell: ({ value }) => <Chip label={value} size="small" variant="outlined" />,
    },
    {
      field: 'enabled',
      headerName: t('common.active'),
      width: 90,
      renderCell: ({ row }) => (
        <Switch
          size="small"
          checked={!!row.enabled}
          onChange={() => { toggleStatus.mutate({ id: row.id, active: !row.enabled }); analytics.track(EVENTS.RULE_TOGGLED, { rule_id: row.id, active: !row.enabled }) }}
        />
      ),
    },
    {
      field: 'created_at',
      headerName: t('device.created'),
      width: 150,
      valueFormatter: (v: string) => (v ? format(new Date(v), 'PP') : '—'),
    },
    {
      field: 'actions_col',
      headerName: t('common.actions'),
      width: 110,
      sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <Tooltip title={t('common.edit')}>
            <IconButton size="small" onClick={() => openEdit(row)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton size="small" color="error" onClick={() => setDeleteTarget(row.id)}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  return (
    <Box>
      <PageHeader
        title={t('rules.title')}
        subtitle={t('rules.createRule')}
        breadcrumbs={[{ label: t('nav.ruleEngine') }, { label: t('rules.title') }]}
        action={
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            {t('rules.createRule')}
          </Button>
        }
      />

      <DataGrid
        rows={rules}
        columns={columns}
        loading={isLoading}
        autoHeight
        pageSizeOptions={[10, 25]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? t('rules.editRule') : t('rules.createRule')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {jsonError && <Alert severity="error">{jsonError}</Alert>}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('rules.title')}
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              required fullWidth autoFocus
            />
            <TextField
              select label={t('rules.eventType')}
              value={form.event_type}
              onChange={(e) => setForm((p) => ({ ...p, event_type: e.target.value }))}
              sx={{ minWidth: 140 }}
            >
              {EVENT_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </TextField>
          </Box>
          <TextField
            label={t('common.description')}
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            fullWidth multiline rows={2}
          />
          <TextField
            label={t('rules.conditions')}
            value={conditionsJson}
            onChange={(e) => setConditionsJson(e.target.value)}
            fullWidth multiline rows={6}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
            helperText='{"operator":"AND","conditions":[{"key":"temperature","op":">","value":80}]}'
          />
          <TextField
            label={t('rules.ruleActions')}
            value={actionsJson}
            onChange={(e) => setActionsJson(e.target.value)}
            fullWidth multiline rows={4}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
            helperText='[{"type":"ALERT","severity":"critical"},{"type":"NOTIFICATION","channel":"websocket"}]'
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.notify_driver}
                onChange={(e) => setForm((p) => ({ ...p, notify_driver: e.target.checked }))}
              />
            }
            label={t('rules.notifyDriver')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={createRule.isPending || updateRule.isPending || !form.name}
          >
            {editing ? t('common.update') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('common.delete')}
        message={t('rules.noRules')}
        onConfirm={() => {
          deleteRule.mutate(deleteTarget!)
          setDeleteTarget(null)
        }}
        onClose={() => setDeleteTarget(null)}
        loading={deleteRule.isPending}
      />
    </Box>
  )
}
